import { vec2, vec3 } from "gl-matrix"
import { Renderer } from "./pipeline/renderer"
import { Texture } from "./backends/webgl/texture"
import { encodePNG } from "./encoders/png-encoder"

export type ColorSamplePurpose = "white-balance" | "point-color"

export interface ColorSampleResult {
	originalPurpose: ColorSamplePurpose
	size: number
	color: vec3
	colorRelative: vec3
	preview?: Blob
	previewData: Uint8Array
}

interface QueuedColorSample {
	purpose: ColorSamplePurpose
	size: number
}

export class ColorSampler {
	renderer: Renderer

	private queuedColorSample: QueuedColorSample | null = null

	private sampleTexture: Texture
	private previewTexture: Texture | null = null

	private colorTexture: Texture

	private colorData = new Uint8Array(4)
	private previewData: Uint8Array

	private lastCallTimestamp: number = 0
	private queuedCall: any = null

	constructor(renderer: Renderer) {
		this.renderer = renderer

		this.colorTexture = renderer.backend.createTexture({
			label: "colorSampler.colorTexture",
			width: 1,
			height: 1,
			format: "rgba8unorm",
		})

		this.sampleTexture = renderer.backend.createTexture({
			label: "colorSampler.sampleTexture",
			width: 1,
			height: 1,
			format: "rgba8unorm",
		})

		this.previewData = new Uint8Array()
	}

	public async sampleThrottled(position: vec2) {
		const now = Date.now()
		const timeSinceLastCall = now - this.lastCallTimestamp
		const msPerFrame = 1000 / 24

		if (timeSinceLastCall >= msPerFrame) {
			this.lastCallTimestamp = now
			return await this.sample(position)
		} else if (!this.queuedCall) {
			this.queuedCall = new Promise((resolve) =>
				setTimeout(async () => {
					this.queuedCall = null
					this.lastCallTimestamp = Date.now()
					resolve(await this.sample(position))
				}, msPerFrame - timeSinceLastCall),
			)
		}

		return (await this.queuedCall) as ColorSampleResult | null
	}

	public async sample(position: vec2): Promise<ColorSampleResult | null> {
		if (!this.queuedColorSample) {
			return null
		}

		const color = this.getColorAtPosition(position)
		const colorRelative: vec3 = [color[0] / 255.0, color[1] / 255.0, color[2] / 255.0]

		const preview = await this.getPreviewAtPosition(position, this.queuedColorSample.size)

		if (!this.queuedColorSample) {
			//May become null during the await
			return null
		}

		return {
			originalPurpose: this.queuedColorSample.purpose,
			size: this.queuedColorSample.size,
			color,
			colorRelative,
			preview,
			previewData: this.previewData,
		}
	}

	public startSampling(purpose: ColorSamplePurpose) {
		let [width, height] = this.renderer.photo.imageState.imageSize

		const maxWidth = Math.max(width >> 1, 2048)
		const maxHeight = Math.max(height >> 1, 2048)
		const scale = Math.min(maxWidth / width, maxHeight / height, 1)

		width *= scale
		height *= scale

		this.sampleTexture.updateSize(width, height)

		let size

		if (purpose == "white-balance") {
			size = 5

			this.renderer.renderWithoutAdjustments(this.renderer.photo, {
				width,
				height,
				output: { format: "rgba", texture: this.sampleTexture },
				colorSpace: "srgb",
			})

			this.previewData = new Uint8Array(size * size * 4)
		} else if (purpose === "point-color") {
			size = 21

			this.renderer.render(this.renderer.photo, {
				width: width,
				height: height,
				output: { format: "rgba", texture: this.sampleTexture },
				colorSpace: "srgb",
			})

			this.previewData = new Uint8Array(size * size * 4)
		}

		this.queuedColorSample = {
			size,
			purpose,
		}
	}

	public stopSampling() {
		this.sampleTexture?.updateSize(1, 1)
		this.queuedColorSample = null
		this.previewTexture = null
	}

	public setPreviewTexture(texture: Texture) {
		this.previewTexture = texture
	}

	/* Private methods */

	private getColorAtPosition(position: vec2): vec3 {
		const renderer = this.renderer

		this.colorTexture.updateSize(1, 1)

		renderer.setRenderTarget(this.colorTexture)
		this.sampleTexture?.bind(0)

		renderer.shaders.colorSampler.setUniforms({
			inputTexture: 0,
			samplePosition: position,
			sampleSize: 1,
		})

		renderer.shaders.colorSampler.draw()

		this.colorTexture.read({ bufferView: this.colorData })

		return [this.colorData[0] ?? 0, this.colorData[1] ?? 0, this.colorData[2] ?? 0]
	}

	private async getPreviewAtPosition(position: vec2, size: number) {
		if (!this.previewTexture) {
			return
		}

		const renderer = this.renderer

		this.colorTexture.updateSize(size, size)

		renderer.setRenderTarget(this.colorTexture)
		this.previewTexture?.bind(0)

		renderer.shaders.colorSampler.setUniforms({
			inputTexture: 0,
			samplePosition: position,
			sampleSize: size,
		})

		renderer.shaders.colorSampler.draw()

		this.colorTexture.read({ bufferView: this.previewData })

		const result = await encodePNG({
			width: size,
			height: size,
			data: this.previewData,
			colorSpace: "display-p3",
		})

		return result
	}
}
